import http from '../services/httpClient';

const STORAGE_KEY = 'recipeReviewDrafts:v1';

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const safeClone = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const readDraftStore = () => {
  if (!canUseStorage()) return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.warn('Failed to read recipe review drafts from localStorage:', error);
    return {};
  }
};

const writeDraftStore = (store) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn('Failed to write recipe review drafts to localStorage:', error);
  }
};

const normalizeBusinessId = (businessId) => String(businessId ?? 'default').trim() || 'default';

const normalizeRecipeId = (value) => {
  if (value === undefined || value === null) return '';
  const normalized = String(value).trim();
  return normalized;
};

const getRecipeCandidateIds = (recipe) => {
  if (!recipe || typeof recipe !== 'object') return [];

  const ids = [
    recipe.menu_item_id,
    recipe.menuItemId,
    recipe.id,
    recipe.recipe_id
  ]
    .map(normalizeRecipeId)
    .filter(Boolean);

  return Array.from(new Set(ids));
};

const makeScopedKey = (businessId, recipeId) => `${normalizeBusinessId(businessId)}::${normalizeRecipeId(recipeId)}`;

const getExistingDraft = (store, businessId, recipe) => {
  const candidateIds = getRecipeCandidateIds(recipe);

  for (const recipeId of candidateIds) {
    const draft = store[makeScopedKey(businessId, recipeId)];
    if (draft) {
      return draft;
    }
  }

  return null;
};

const getDraftIds = (recipe, existingDraft) => {
  const candidateIds = getRecipeCandidateIds(recipe);
  const storedIds = Array.isArray(existingDraft?.recipeIds)
    ? existingDraft.recipeIds.map(normalizeRecipeId).filter(Boolean)
    : [];

  return Array.from(new Set([...candidateIds, ...storedIds]));
};

// ── Server sync (SP7) ────────────────────────────────────────────────────────
// The synchronous localStorage interface below is the contract both screens and the
// review PDF depend on — it stays. The server (RecipeReviewDrafts) is the durable,
// cross-device truth layered on top: hydrate pulls it on screen load (newest wins),
// and every local save/clear pushes fire-and-forget. A dead network degrades to the
// old localStorage-only behaviour instead of breaking the screen.

const firstNumericRecipeId = (recipeIds) => {
  for (const id of Array.isArray(recipeIds) ? recipeIds : []) {
    const parsed = parseInt(id, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const pushDraftToServer = (recipeIds, payload) => {
  const recipeId = firstNumericRecipeId(recipeIds);
  if (recipeId === null) return;
  http.put(`/recipes/${recipeId}/review-draft`, { payload }).catch((error) => {
    console.warn('Review draft server sync failed (kept locally):', error?.message || error);
  });
};

const deleteDraftOnServer = (recipeIds) => {
  for (const id of Array.isArray(recipeIds) ? recipeIds : []) {
    const parsed = parseInt(id, 10);
    if (!Number.isFinite(parsed)) continue;
    http.delete(`/recipes/${parsed}/review-draft`).catch((error) => {
      console.warn('Review draft server delete failed:', error?.message || error);
    });
  }
};

/**
 * Pull the business's server drafts into the local store. Newest wins per recipe:
 * a local draft edited after the server copy is pushed back up instead of clobbered.
 * Call on screen load, then re-run mergeRecipesWithLocalReviewDrafts.
 *
 * @returns {Promise<number>} drafts applied locally from the server
 */
export const hydrateRecipeReviewDrafts = async (businessId) => {
  let response;
  try {
    response = await http.get('/recipes/review-drafts');
  } catch (error) {
    console.warn('Review draft hydrate failed (local drafts still apply):', error?.message || error);
    return 0;
  }
  const serverDrafts = Array.isArray(response?.data) ? response.data : [];
  if (serverDrafts.length === 0) return 0;

  const store = readDraftStore();
  let applied = 0;

  for (const row of serverDrafts) {
    const recipeId = normalizeRecipeId(row.recipe_id);
    if (!recipeId) continue;
    const serverPayload = row.payload && typeof row.payload === 'object' ? row.payload : null;
    if (!serverPayload) continue;
    const serverUpdatedAt = Number(row.updated_at) || Number(serverPayload.updatedAt) || 0;

    const local = store[makeScopedKey(businessId, recipeId)];
    const localUpdatedAt = Number(local?.updatedAt) || 0;

    if (local && localUpdatedAt > serverUpdatedAt) {
      // Local edit is newer — the server learns about it, not the other way round.
      pushDraftToServer(getDraftIds({ id: recipeId }, local), local);
      continue;
    }

    const keys = Array.from(new Set([
      recipeId,
      ...(Array.isArray(serverPayload.recipeIds) ? serverPayload.recipeIds.map(normalizeRecipeId).filter(Boolean) : []),
    ]));
    const payload = { ...safeClone(serverPayload), recipeIds: keys, updatedAt: serverUpdatedAt || Date.now() };
    for (const key of keys) {
      store[makeScopedKey(businessId, key)] = payload;
    }
    applied++;
  }

  if (applied > 0) writeDraftStore(store);
  return applied;
};

export const getRecipeReviewDraft = (recipe, businessId) => {
  const store = readDraftStore();
  const draft = getExistingDraft(store, businessId, recipe);
  return draft ? safeClone(draft) : null;
};

export const saveRecipeReviewDraft = ({ recipe, businessId, draft }) => {
  const store = readDraftStore();
  const existingDraft = getExistingDraft(store, businessId, recipe);
  const recipeIds = getDraftIds(recipe, existingDraft);

  if (recipeIds.length === 0) {
    return null;
  }

  const payload = {
    ...safeClone(draft),
    recipeIds,
    updatedAt: Date.now()
  };

  for (const recipeId of recipeIds) {
    store[makeScopedKey(businessId, recipeId)] = payload;
  }

  writeDraftStore(store);
  pushDraftToServer(recipeIds, payload);
  return safeClone(payload);
};

export const clearRecipeReviewDraft = (recipe, businessId) => {
  const store = readDraftStore();
  const existingDraft = getExistingDraft(store, businessId, recipe);
  const recipeIds = getDraftIds(recipe, existingDraft);

  if (recipeIds.length === 0) {
    return false;
  }

  let changed = false;

  for (const recipeId of recipeIds) {
    const scopedKey = makeScopedKey(businessId, recipeId);
    if (scopedKey in store) {
      delete store[scopedKey];
      changed = true;
    }
  }

  if (changed) {
    writeDraftStore(store);
  }
  // Server delete regardless of local hit: another device may hold the row.
  deleteDraftOnServer(recipeIds);

  return changed;
};

export const mergeRecipesWithLocalReviewDrafts = (recipes, businessId) => {
  if (!Array.isArray(recipes) || recipes.length === 0) {
    return Array.isArray(recipes) ? recipes : [];
  }

  const store = readDraftStore();

  return recipes.map((recipe) => {
    const hasDraft = getRecipeCandidateIds(recipe).some((recipeId) => store[makeScopedKey(businessId, recipeId)]);

    if (!hasDraft) {
      return recipe?.hasLocalReviewDraft
        ? { ...recipe, hasLocalReviewDraft: false }
        : recipe;
    }

    return {
      ...recipe,
      status: 'draft',
      hasLocalReviewDraft: true
    };
  });
};

const normalizeIngredientKey = (value) => String(value || '').trim().toLowerCase();

/**
 * Rewrite an ingredient name across EVERY saved draft for a business.
 *
 * A rename or merge in the inventory is global — it affects every recipe, not just
 * the one on screen. Drafts are read back in preference to the server
 * (see itemmap's fetchIngredients), so without this sweep a draft written before
 * the change would resurrect the old name the next time that recipe is opened.
 *
 * mode 'rename' : every row named `from` becomes `to`.
 * mode 'merge'  : rows named `from` are DROPPED when the same draft already has a
 *                 `to` row, otherwise renamed to `to`. This mirrors the backend
 *                 merge (duplicatePreventionService.mergeItems), which deletes the
 *                 duplicate's recipe row when the master is already present and
 *                 re-points it otherwise.
 *
 * @returns {number} how many drafts were modified
 */
export const applyIngredientNameChangeToDrafts = (businessId, { from, to, mode = 'rename', newUnit } = {}) => {
  const fromKey = normalizeIngredientKey(from);
  const toName = String(to || '').trim();
  if (!fromKey || (!toName && mode !== 'unit')) return 0;

  const store = readDraftStore();
  const normalizedBusinessId = normalizeBusinessId(businessId);
  let changedDrafts = 0;

  for (const [scopedKey, draft] of Object.entries(store)) {
    if (!draft || typeof draft !== 'object') continue;

    const separatorIndex = scopedKey.indexOf('::');
    if (separatorIndex < 0) continue;
    if (normalizeBusinessId(scopedKey.slice(0, separatorIndex)) !== normalizedBusinessId) continue;
    if (!Array.isArray(draft.ingredientList)) continue;

    const rows = draft.ingredientList;
    const hasTarget = rows.some((row) => normalizeIngredientKey(row?.name) === normalizeIngredientKey(toName));
    let touched = false;
    const next = [];

    for (const row of rows) {
      if (normalizeIngredientKey(row?.name) !== fromKey) {
        next.push(row);
        continue;
      }
      if (mode === 'unit') {
        next.push({ ...row, unit: newUnit || row?.unit });
        touched = true;
        continue;
      }
      if (mode === 'merge' && hasTarget) {
        touched = true; // drop the duplicate row
        continue;
      }
      next.push({ ...row, name: toName });
      touched = true;
    }

    if (touched) {
      const updated = { ...draft, ingredientList: next };
      store[scopedKey] = updated;
      changedDrafts++;
      pushDraftToServer(getDraftIds({ id: scopedKey.slice(separatorIndex + 2) }, updated), updated);
    }
  }

  if (changedDrafts > 0) writeDraftStore(store);
  return changedDrafts;
};

export const listRecipeReviewDrafts = (businessId) => {
  const store = readDraftStore();
  const normalizedBusinessId = normalizeBusinessId(businessId);
  const latestByRecipeSet = new Map();

  Object.entries(store).forEach(([scopedKey, draft]) => {
    if (!draft || typeof draft !== 'object') return;

    const separatorIndex = scopedKey.indexOf('::');
    if (separatorIndex < 0) return;

    const scopedBusinessId = normalizeBusinessId(scopedKey.slice(0, separatorIndex));
    if (scopedBusinessId !== normalizedBusinessId) return;

    const scopedRecipeId = normalizeRecipeId(scopedKey.slice(separatorIndex + 2));
    const draftRecipeIds = Array.isArray(draft.recipeIds)
      ? draft.recipeIds.map(normalizeRecipeId).filter(Boolean)
      : [];

    const normalizedRecipeIds = Array.from(new Set([
      ...draftRecipeIds,
      scopedRecipeId
    ].filter(Boolean))).sort();

    if (normalizedRecipeIds.length === 0) return;

    const dedupeKey = normalizedRecipeIds.join('|');
    const normalizedUpdatedAt = Number(draft.updatedAt) || 0;
    const existing = latestByRecipeSet.get(dedupeKey);

    if (!existing || normalizedUpdatedAt >= existing.updatedAt) {
      latestByRecipeSet.set(dedupeKey, {
        ...safeClone(draft),
        recipeIds: normalizedRecipeIds,
        updatedAt: normalizedUpdatedAt
      });
    }
  });

  return Array.from(latestByRecipeSet.values()).sort(
    (a, b) => (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0)
  );
};